import { dualClockStamp } from "./clock";
import { crewIdFromName, currentSession, type DutyRoster, type DutySession } from "./duty";
import { blockTimeMin } from "./flightlog";
import type { DeskRole, FlightLogEntry, LogbookRole, PilotLogbookEntry } from "./types";

export const LOGBOOK_ROLE_LABEL: Record<LogbookRole, string> = {
  PIC: "PIC",
  SIC: "SIC",
  dual: "Dual",
  SOLO: "Solo",
};

export function newLogbookId(now = new Date()): string {
  return `lb-${now.getTime().toString(36)}-${Math.floor(Math.random() * 1e4).toString(36)}`;
}

function finiteMin(value: unknown): number | undefined {
  if (typeof value !== "number" || !Number.isFinite(value) || value < 0) return undefined;
  return Math.round(value);
}

/** Air minutes keep a tenth so 1.46 h stays 1.46 h, not 1.47. */
function finiteTenth(value: unknown): number | undefined {
  if (typeof value !== "number" || !Number.isFinite(value) || value < 0) return undefined;
  return Math.round(value * 10) / 10;
}

/** Shown under the logbook form. Empty fields used to fail with no message. */
export function logbookFormError(input: {
  originIcao?: string;
  destIcao?: string;
  flightTimeMin?: number;
}): string | null {
  if (!input.originIcao?.trim() || !input.destIcao?.trim()) return "Origin and destination are required.";
  if (input.flightTimeMin == null || !Number.isFinite(input.flightTimeMin) || input.flightTimeMin <= 0) {
    return "Flight time is required.";
  }
  return null;
}

export function clampLogMinutes(value: unknown, max?: number): number {
  if (typeof value !== "number" || !Number.isFinite(value) || value < 0) return 0;
  const rounded = Math.round(value);
  if (max != null && Number.isFinite(max)) return Math.min(rounded, Math.max(0, Math.round(max)));
  return rounded;
}

/** Night / IFR cannot exceed flight time. */
export function normalizeLogbookTimes(input: {
  flightTimeMin: number;
  nightMin?: number;
  ifrMin?: number;
}): { flightTimeMin: number; nightMin: number; ifrMin: number } {
  const flightTimeMin = clampLogMinutes(input.flightTimeMin);
  return {
    flightTimeMin,
    nightMin: clampLogMinutes(input.nightMin, flightTimeMin),
    ifrMin: clampLogMinutes(input.ifrMin, flightTimeMin),
  };
}

export function hoursFromMinutes(min: number): number {
  return Math.round((min / 60) * 100) / 100;
}

export function formatLogbookHours(min: number): string {
  if (!Number.isFinite(min) || min <= 0) return "0.0";
  return hoursFromMinutes(min).toFixed(1);
}

export function logbookTotals(entries: PilotLogbookEntry[]): {
  flights: number;
  flightTimeMin: number;
  nightMin: number;
  ifrMin: number;
} {
  return entries.reduce(
    (acc, row) => {
      const times = normalizeLogbookTimes(row);
      acc.flights += 1;
      acc.flightTimeMin += times.flightTimeMin;
      acc.nightMin += times.nightMin;
      acc.ifrMin += times.ifrMin;
      return acc;
    },
    { flights: 0, flightTimeMin: 0, nightMin: 0, ifrMin: 0 },
  );
}

export function hydrateLogbookEntry(raw: Partial<PilotLogbookEntry>): PilotLogbookEntry | null {
  const crewName = typeof raw.crewName === "string" ? raw.crewName.trim() : "";
  const originIcao = (raw.originIcao ?? "").trim().toUpperCase();
  const destIcao = (raw.destIcao ?? "").trim().toUpperCase();
  if (!crewName || !originIcao || !destIcao) return null;
  const times = normalizeLogbookTimes({
    flightTimeMin: raw.flightTimeMin ?? 0,
    nightMin: raw.nightMin,
    ifrMin: raw.ifrMin,
  });
  const role: LogbookRole =
    raw.role === "SIC" || raw.role === "dual" || raw.role === "SOLO" || raw.role === "PIC" ? raw.role : "PIC";
  const crewId = raw.crewId?.trim() || crewIdFromName(crewName);
  return {
    id: raw.id || newLogbookId(),
    crewId,
    crewName,
    dateUtc: raw.dateUtc || new Date().toISOString(),
    originIcao,
    destIcao,
    route: raw.route?.trim() || `${originIcao}–${destIcao}`,
    registration: (raw.registration ?? "").trim().toUpperCase(),
    aircraftType: (raw.aircraftType ?? "").trim().toUpperCase(),
    role,
    flightTimeMin: times.flightTimeMin,
    airTimeMin: finiteTenth(raw.airTimeMin),
    landingsDay: finiteMin(raw.landingsDay),
    landingsNight: finiteMin(raw.landingsNight),
    nightMin: times.nightMin || undefined,
    ifrMin: times.ifrMin || undefined,
    notes: (raw.notes ?? "").trim(),
    flightLogId: raw.flightLogId,
    dutySessionId: raw.dutySessionId,
    createdAtUtc: raw.createdAtUtc || raw.dateUtc || new Date().toISOString(),
  };
}

export function hydrateLogbook(list: PilotLogbookEntry[] | undefined): PilotLogbookEntry[] {
  if (!Array.isArray(list)) return [];
  return list.map(hydrateLogbookEntry).filter((row): row is PilotLogbookEntry => Boolean(row));
}

export function createLogbookEntry(input: {
  crewId?: string;
  crewName: string;
  dateUtc?: string;
  originIcao: string;
  destIcao: string;
  registration: string;
  aircraftType?: string;
  role?: LogbookRole;
  flightTimeMin: number;
  airTimeMin?: number;
  landingsDay?: number;
  landingsNight?: number;
  nightMin?: number;
  ifrMin?: number;
  notes?: string;
  flightLogId?: string;
  dutySessionId?: string;
  now?: Date;
}): PilotLogbookEntry {
  const now = input.now ?? new Date();
  const times = normalizeLogbookTimes(input);
  const originIcao = input.originIcao.trim().toUpperCase();
  const destIcao = input.destIcao.trim().toUpperCase();
  const crewName = input.crewName.trim();
  return {
    id: newLogbookId(now),
    crewId: input.crewId?.trim() || crewIdFromName(crewName),
    crewName,
    dateUtc: input.dateUtc || now.toISOString(),
    originIcao,
    destIcao,
    route: `${originIcao}–${destIcao}`,
    registration: input.registration.trim().toUpperCase(),
    aircraftType: (input.aircraftType ?? "").trim().toUpperCase(),
    role: input.role ?? "PIC",
    flightTimeMin: times.flightTimeMin,
    airTimeMin: finiteTenth(input.airTimeMin),
    landingsDay: finiteMin(input.landingsDay),
    landingsNight: finiteMin(input.landingsNight),
    nightMin: times.nightMin || undefined,
    ifrMin: times.ifrMin || undefined,
    notes: (input.notes ?? "").trim(),
    flightLogId: input.flightLogId,
    dutySessionId: input.dutySessionId,
    createdAtUtc: now.toISOString(),
  };
}

export function logbookFromFlightLog(
  log: FlightLogEntry,
  input: {
    crewId?: string;
    crewName?: string;
    role?: LogbookRole;
    flightTimeMin?: number;
    airTimeMin?: number;
    landingsDay?: number;
    landingsNight?: number;
    nightMin?: number;
    ifrMin?: number;
    dutySessionId?: string;
    aircraftType?: string;
    now?: Date;
  } = {},
): PilotLogbookEntry {
  const block = blockTimeMin(log);
  const planned = log.plannedEteMin && log.plannedEteMin > 0 ? log.plannedEteMin : 0;
  return createLogbookEntry({
    crewId: input.crewId,
    crewName: input.crewName?.trim() || log.assignedPilot || "Crew",
    dateUtc: log.offBlocksUtc || log.arrivedAtUtc || log.departedAtUtc || log.createdAtUtc,
    originIcao: log.originIcao,
    destIcao: log.destIcao,
    registration: log.registration,
    aircraftType: input.aircraftType,
    role: input.role ?? "PIC",
    flightTimeMin: input.flightTimeMin ?? block ?? planned,
    airTimeMin: input.airTimeMin ?? log.airTimeMin,
    landingsDay: input.landingsDay ?? log.landingsDay,
    landingsNight: input.landingsNight ?? log.landingsNight,
    nightMin: input.nightMin,
    ifrMin: input.ifrMin,
    notes: log.notes,
    flightLogId: log.id,
    dutySessionId: input.dutySessionId,
    now: input.now,
  });
}

export function logsForPilot(
  entries: PilotLogbookEntry[],
  role: DeskRole,
  crewId: string,
  crewName: string,
): PilotLogbookEntry[] {
  if (role === "dispatcher") return entries;
  const id = crewId.trim().toLowerCase();
  const name = crewName.trim().toLowerCase();
  return entries.filter(
    (e) => e.crewId.toLowerCase() === id || e.crewName.trim().toLowerCase() === name,
  );
}

export function dutySessionForLogbook(roster: DutyRoster, crewId: string): DutySession | undefined {
  return currentSession(roster.sessions, crewId);
}

export function dutyHistoryForCrew(roster: DutyRoster, crewId: string): DutySession[] {
  return roster.sessions.filter((s) => s.crewId === crewId);
}

export function stampLogbookNow(now = new Date()): { utc: string; local: string } {
  const stamp = dualClockStamp(now);
  return { utc: stamp.utc, local: stamp.local };
}
